import { signal } from '@angular/core';
import { debounce, FieldTree, min, required, SchemaPathTree } from '@angular/forms/signals';
import { EFLDto, EFLDtoOptionDto } from '../../../core/mceapi.client.generated';
import { appOpt, getNoneOptionDto, getSomeOptionDto } from '../../../core/mceapi.client.helpers';
import { match, none, Option, some } from '../../../shared/functions/functional';


export interface EarthFaultLoop {
  eflRequired: boolean;
  userMaxDisconnectionTime: boolean;
  userMaxDisconnectionTimeValue: number;
  userIa: boolean;
  userIaValue: number;
  userZs: boolean;
  userZsValue: number;
}

export function buildEarthFaultLoopSchema(path: SchemaPathTree<EarthFaultLoop>) {

  required(path.userMaxDisconnectionTimeValue, { message: `Disconnection time is required` });
  min(path.userMaxDisconnectionTimeValue, 0, { message: `Disconnection time must be positive` });
  debounce(path.userMaxDisconnectionTimeValue, 500);

  required(path.userIaValue, { message: `Ia is required` });
  min(path.userIaValue, 0, { message: `Ia must be positive` });
  debounce(path.userIaValue, 500);

  required(path.userZsValue, { message: `Zs is required` });
  min(path.userZsValue, 0, { message: `Zs must be positive` });
  debounce(path.userZsValue, 500);

}

export function createEarthFaultLoop(dto: EFLDtoOptionDto) {
  const efl = appOpt(dto);
  return signal<EarthFaultLoop>(
    match(
      () => ({
        eflRequired: false,
        userMaxDisconnectionTime: false,
        userMaxDisconnectionTimeValue: 0,
        userIa: false,
        userIaValue: 0,
        userZs: false,
        userZsValue: 0,
        regulation411_3_1_2_bonding: false }) as EarthFaultLoop,
      (x: EFLDto) => {
        if (x.userMaxDisconnectionTime === undefined
          || x.userMaxDisconnectionTimeValue === undefined
          || x.userIa === undefined
          || x.userIaValue === undefined
          || x.userZs === undefined
          || x.userZsValue === undefined) {
          throw new Error('Earth fault loop DTO is missing required values.');
        }

        return {
          eflRequired: true,
          userMaxDisconnectionTime: x.userMaxDisconnectionTime,
          userMaxDisconnectionTimeValue: x.userMaxDisconnectionTimeValue,
          userIa: x.userIa,
          userIaValue: x.userIaValue,
          userZs: x.userZs,
          userZsValue: x.userZsValue
        };
      }
    )(efl))
}

export function createEarthFaultLoopDto(model: FieldTree<EarthFaultLoop>) {
  return signal<Option<EFLDto>>(
    model.eflRequired().value()
      ? some({
          userMaxDisconnectionTime: model.userMaxDisconnectionTime().value(),
          userMaxDisconnectionTimeValue: model.userMaxDisconnectionTimeValue().value(),
          userIa: model.userIa().value(),
          userIaValue: model.userIaValue().value(),
          userZs: model.userZs().value(),
          userZsValue: model.userZsValue().value() })
      : none);
}

